import { supabase } from '@/shared/lib/supabase';
import { logger } from '@/shared/lib/logger';

export const notificationKeys = {
  all: ['notifications'] as const,
  list: () => [...notificationKeys.all, 'list'] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
  prefs: () => [...notificationKeys.all, 'prefs'] as const,
};

export type NotificationKind =
  | 'expense_created'
  | 'expense_updated'
  | 'expense_commented'
  | 'added_to_group'
  | 'settlement_received'
  | 'recurring_generated'
  | 'moderation_pending'
  | 'invitation_received';

export interface NotificationRow {
  id: string;
  recipient_id: string;
  kind: NotificationKind;
  group_id: string | null;
  expense_id: string | null;
  settlement_id: string | null;
  actor_participant_id: string | null;
  metadata: Record<string, unknown>;
  read_at: string | null;
  created_at: string;
  // K.1: snapshot de la preferencia in-app al momento del evento. El badge y
  // la lista solo muestran filas con inapp=true; email/push son independientes.
  inapp: boolean;
}

export interface NotificationWithContext extends NotificationRow {
  actor_name: string | null;
  group_name: string | null;
  // K.2: estado del destino. Si el grupo/gasto al que apunta la notificación
  // está soft-deleted, la UI evita navegar a una pantalla rota.
  group_deleted: boolean;
  expense_deleted: boolean;
}

export async function fetchNotifications(limit = 30): Promise<NotificationWithContext[]> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) throw new Error('Not authenticated');
  const { data: me } = await supabase
    .from('participants')
    .select('id')
    .eq('auth_user_id', userData.user.id)
    .maybeSingle();
  if (!me) return [];

  const { data, error } = await supabase
    .from('notifications')
    .select(`
      *,
      actor:participants!notifications_actor_participant_id_fkey(display_name),
      group:groups(name, deleted_at),
      expense:expenses(deleted_at)
    `)
    .eq('recipient_id', me.id)
    // K.1: solo las que el usuario quiere in-app. Las filas con inapp=false
    // existen para email/push (auditoría del evento) pero no se muestran aquí.
    .eq('inapp', true)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    logger.error('fetchNotifications failed', { code: error.code });
    throw new Error(error.message);
  }

  return (data ?? []).map((row) => {
    const r = row as unknown as {
      actor: { display_name: string } | null;
      group: { name: string; deleted_at: string | null } | null;
      expense: { deleted_at: string | null } | null;
    };
    return {
      ...(row as unknown as NotificationRow),
      actor_name: r.actor?.display_name ?? null,
      group_name: r.group?.name ?? null,
      group_deleted: r.group?.deleted_at != null,
      expense_deleted: r.expense?.deleted_at != null,
    };
  });
}

export async function fetchUnreadCount(): Promise<number> {
  const { data: profile } = await supabase.auth.getUser();
  if (!profile.user) return 0;
  const { data: me } = await supabase
    .from('participants')
    .select('id')
    .eq('auth_user_id', profile.user.id)
    .maybeSingle();
  if (!me) return 0;

  const { count, error } = await supabase
    .from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('recipient_id', me.id)
    // K.1: el badge cuenta solo las no-leídas que el usuario quiere in-app.
    .eq('inapp', true)
    .is('read_at', null);
  if (error) {
    logger.error('fetchUnreadCount failed', { code: error.code });
    return 0;
  }
  return count ?? 0;
}

export async function markAllRead(): Promise<void> {
  const { error } = await supabase.from('notifications')
    .update({ read_at: new Date().toISOString() })
    .is('read_at', null);
  if (error) throw new Error(error.message);
}

export async function markOneRead(id: string): Promise<void> {
  const { error } = await supabase.from('notifications')
    .update({ read_at: new Date().toISOString() })
    .eq('id', id).is('read_at', null);
  if (error) throw new Error(error.message);
}

// ==================== Preferences ====================

export interface NotificationPrefs {
  participant_id: string;
  expense_min_amount_cents: number;
  expense_created_inapp: boolean;
  expense_created_push: boolean;
  expense_created_email: boolean;
  expense_updated_inapp: boolean;
  expense_updated_push: boolean;
  expense_updated_email: boolean;
  expense_commented_inapp: boolean;
  expense_commented_push: boolean;
  expense_commented_email: boolean;
  invitation_received_inapp: boolean;
  invitation_received_push: boolean;
  invitation_received_email: boolean;
  added_to_group_inapp: boolean;
  added_to_group_push: boolean;
  added_to_group_email: boolean;
  settlement_received_inapp: boolean;
  settlement_received_push: boolean;
  settlement_received_email: boolean;
  recurring_generated_inapp: boolean;
  recurring_generated_push: boolean;
  recurring_generated_email: boolean;
  moderation_pending_inapp: boolean;
  moderation_pending_push: boolean;
  moderation_pending_email: boolean;
}

export async function fetchMyPrefs(): Promise<NotificationPrefs | null> {
  const { data: profile } = await supabase.auth.getUser();
  if (!profile.user) return null;
  const { data: participant } = await supabase.from('participants')
    .select('id').eq('auth_user_id', profile.user.id).maybeSingle();
  if (!participant) return null;

  // Usa RPC get_notification_prefs que crea el registro si no existe
  const { data, error } = await supabase.rpc('get_notification_prefs', {
    p_participant_id: participant.id,
  });
  if (error) {
    logger.error('fetchMyPrefs failed', { code: error.code });
    return null;
  }
  return data as NotificationPrefs;
}

export async function updateMyPrefs(patch: Partial<NotificationPrefs>): Promise<void> {
  const { data: profile } = await supabase.auth.getUser();
  if (!profile.user) throw new Error('Not authenticated');
  const { data: participant } = await supabase.from('participants')
    .select('id').eq('auth_user_id', profile.user.id).maybeSingle();
  if (!participant) throw new Error('No participant');

  const { error } = await supabase.from('notification_preferences')
    .update(patch).eq('participant_id', participant.id);
  if (error) throw new Error(error.message);
}
